import { useState, useEffect, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { ShieldAlert, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState("verifying");
  const [message, setMessage] = useState("");
  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    if (!token) {
      setStatus("error");
      setMessage("No verification token found. Please check your email link.");
      return;
    }

    const verify = async () => {
      try {
        const res = await axios.get(`${API}/auth/verify-email?token=${token}`);
        setStatus("success");
        setMessage(res.data.message);
      } catch (err) {
        setStatus("error");
        setMessage(err.response?.data?.detail || "Verification failed. The link may be expired or invalid.");
      }
    };
    verify();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 p-4">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center gap-2 mb-8">
          <div className="relative">
            <div className="absolute -inset-1 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-2xl blur-sm" />
            <div className="relative h-10 w-10 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
              <ShieldAlert className="h-5 w-5 text-blue-400" />
            </div>
          </div>
          <span className="text-xl font-bold text-slate-100 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            Callintell<span className="text-blue-400">™</span>
          </span>
        </div>

        <Card className="bg-slate-900/60 border-slate-800/50 shadow-2xl">
          <CardContent className="p-8 text-center">
            {status === "verifying" && (
              <div className="space-y-4" data-testid="verify-loading">
                <Loader2 className="h-12 w-12 animate-spin text-blue-400 mx-auto" />
                <h2 className="text-xl font-bold text-slate-100" style={{ fontFamily: "Outfit, sans-serif" }}>
                  Verifying your email...
                </h2>
                <p className="text-sm text-slate-400">Please wait while we confirm your email address.</p>
              </div>
            )}

            {status === "success" && (
              <div className="space-y-4" data-testid="verify-success">
                <div className="h-16 w-16 rounded-full bg-emerald-500/10 border-2 border-emerald-500/30 flex items-center justify-center mx-auto">
                  <CheckCircle2 className="h-8 w-8 text-emerald-400" />
                </div>
                <h2 className="text-xl font-bold text-slate-100" style={{ fontFamily: "Outfit, sans-serif" }}>
                  Email Verified!
                </h2>
                <p className="text-sm text-slate-400">{message}</p>
                <Link to="/login">
                  <Button className="w-full h-11 bg-blue-600 hover:bg-blue-500 text-white font-semibold mt-4" data-testid="go-to-login">
                    Go to Login
                  </Button>
                </Link>
              </div>
            )}

            {status === "error" && (
              <div className="space-y-4" data-testid="verify-error">
                <div className="h-16 w-16 rounded-full bg-red-500/10 border-2 border-red-500/30 flex items-center justify-center mx-auto">
                  <XCircle className="h-8 w-8 text-red-400" />
                </div>
                <h2 className="text-xl font-bold text-slate-100" style={{ fontFamily: "Outfit, sans-serif" }}>
                  Verification Failed
                </h2>
                <p className="text-sm text-slate-400">{message}</p>
                <Link to="/login">
                  <Button className="w-full h-11 bg-slate-800 hover:bg-slate-700 text-white font-semibold mt-4" data-testid="back-to-login">
                    Back to Login
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
